<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-male"></i>
          {{ $label("user.users", "Users") }}
          <router-link
            class="btn btn-v-lightgreen-white pull-right"
            :to="{ name: 'NewUser' }"
            v-tooltip="{
              content: $label('user.create_new_user', 'Create new user'),
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label("user.user_failed_request", "Request failed while loading user.")
      }}
    </alert-box>

    <Table
      v-show="!failedRequest"
      id="users-table"
      :loading="loading"
      :columns="[
        {
          title: 'Name',
          id: row => `user-name-${row.id}`,
          sorting: true,
          value: row => `${row.last_name} ${row.first_name}`
        },
        {
          title: 'Email',
          id: row => `user-email-${row.id}`,
          field: 'email'
        },
        {
          title: 'Permissions',
          field: 'permissions'
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'UserDetails',
              params: { user_id: row.id }
            });
          }
        },
        {
          type: 'delete',
          id: row => `delete-user-button-${row.id}`,
          onClick: row => deleteUser(row.id)
        },
        {
          icon: 'envelope-o',
          iconSize: 'lg',
          bgColor: 'primary',
          onClick: () => {
            $router.push('/settings/users/id/message/');
          }
        }
      ]"
      :data="users"
      :meta="metaData"
      selectable
      @onRowSelect="() => {}"
      @fetchQuery="
        params => {
          fetchParams = params;
          getUsers({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewUser' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{
                $label("user.create_new_user", "Create new user")
              }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";

import AlertBox from "@/components/Common/Alerts/AlertBox";
import Table from "@/components/Common/Table";

export default {
  name: "Users.vue",

  components: {
    AlertBox,
    Table
  },

  data() {
    return {
      users: [],
      loading: false,
      updating: false,
      failedRequest: false,
      selectedUserId: null,
      componentKey: 0,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      }
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.user_deleted === true ||
        vm.$route.query.user_deleted === "true"
      ) {
        swal("Success!", "The user was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getUsers(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.user_deleted === true) {
      swal("Success!", "The user was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (this.to % this.perPage === 1 && this.currentPage !== 1) {
      this.currentPage -= 1;
    }
    this.getUsers(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getUsers(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.users = [];
      const endpoint = this.$partnerEndpoint("users");

      Api()
        .get(endpoint, { params: this.fetchParams })
        .then(response => {
          this.users = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteUser(userId) {
      this.selectedUserId = userId;
      if (this.loading || this.updating || this.selectedUserId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The user will be deleted and will not be able to use his account",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "users/" + this.selectedUserId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "UsersOverview",
                  query: { user_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The user was deleted.", "success");
                    this.getUsers(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete users.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
              if (error.response.status === 409) {
                swal({
                  title: "Your own account",
                  text: "You cannot delete your own account.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.checkbox-fixed-width {
  width: 50px;
}

.v-table-checkbox-label {
  margin-left: 10px;
}

.v-table-checkbox {
  position: relative !important;
}

.min-width-auto {
  width: 50px;
}

.pagination-select {
  font-weight: 700;
  height: 34px;
  padding-left: 10px;
  width: 70px;
  cursor: pointer;
  border: 1px solid #ddd;
  color: #666;
  outline: none;
}
</style>
